import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ComponentPropsWithoutRef } from "react";
import { createPortal } from "react-dom";
import { BLOCK_ACTIONS_Z_INDEX } from "./constants";
import { useCanvasToolsPositions } from "./useCanvasToolsPositions";

export interface CanvasToolsBlockActionsProps
  extends ComponentPropsWithoutRef<"div"> {
  showInsideWhenFirstBlock?: boolean;
}

const TOP_OFFSET = -38; // Match with hover spacing
const BOTTOM_OFFSET = 10; // Offset when showing at bottom

const calculateTopOffset = (
  showInsideWhenFirstBlock: boolean,
  isFirstBlock: boolean,
): number => {
  // Show at bottom for first block
  if (showInsideWhenFirstBlock && isFirstBlock) {
    return BOTTOM_OFFSET;
  }

  // Normal positioning (above the block)
  return TOP_OFFSET;
};

export const CanvasToolsBlockActions = ({
  children,
  showInsideWhenFirstBlock = true,
}: CanvasToolsBlockActionsProps) => {
  const [position, setPosition] = useState<CSSProperties>({});
  const actionsRef = useRef<HTMLDivElement>(null);

  const { selectedBlock, positions, isFirstBlock } = useCanvasToolsPositions();

  useEffect(() => {
    if (!positions) {
      return;
    }

    const actionsWidth = actionsRef.current?.offsetWidth || 0;

    // Calculate top offset based on positioning requirements
    const topOffset = calculateTopOffset(
      showInsideWhenFirstBlock,
      isFirstBlock,
    );

    setPosition({
      position: "absolute",
      top: positions.elementRect.top + topOffset,
      left: positions.elementRect.right - actionsWidth,
      zIndex: BLOCK_ACTIONS_Z_INDEX,
      pointerEvents: "auto",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when positions change
  }, [positions]);

  if (!selectedBlock) {
    return null;
  }

  const blockActionsElement = (
    <div
      ref={actionsRef}
      className="bg-circle pointer-events-auto flex items-center gap-0.5 rounded p-1"
      style={position}
    >
      {children}
    </div>
  );

  // Find the CanvasCard container to portal to (this has the right coordinate system)
  const canvasCard = document.getElementById("builder-ui-canvas-card");
  if (!canvasCard) {
    return blockActionsElement;
  }

  return createPortal(blockActionsElement, canvasCard);
};
